<template>
  <div>
    <b-modal
      hide-footer
      hide-backdrop
      no-close-on-esc
      :title="modalTitle"
      :ref="modalId"
      :id="modalId"
      content-class="counter-type-modal"
      body-class="counter-type-modal-body"
      header-class="counter-type-modal-header"
      size="md"
      @shown="onModalShown"
    >
      <p class="ct-help">
        <template v-if="isFnbScope">
          Filter F&amp;B items by item Con. Area. Choose
          <strong>All Items</strong> to show everything.
        </template>
        <template v-else>
          Filter showtimes by screen Con. Area. Choose
          <strong>All Schedules</strong> to show everything.
        </template>
      </p>

      <div class="ct-options" v-if="!isLoading">
        <button
          type="button"
          class="ct-option"
          :class="{ active: selectedCounterType === null }"
          @click="selectAll"
        >
          <span class="ct-option-radio" aria-hidden="true"></span>
          <span class="ct-option-text">
            <span class="ct-option-title">{{
              isFnbScope ? "All Items" : "All Schedules"
            }}</span>
            <span class="ct-option-sub">{{
              isFnbScope
                ? "No filter — show every F&B item"
                : "No filter — show every showtime"
            }}</span>
          </span>
        </button>

        <button
          type="button"
          v-for="option in counterTypeOptions"
          :key="option.fct_id"
          class="ct-option"
          :class="{
            active:
              selectedCounterType &&
              String(selectedCounterType.fct_id) === String(option.fct_id),
          }"
          @click="selectOption(option)"
        >
          <span class="ct-option-radio" aria-hidden="true"></span>
          <span class="ct-option-text">
            <span class="ct-option-title">{{ option.counter_type }}</span>
            <span class="ct-option-sub">{{
              isFnbScope
                ? "Only items mapped to this Con. Area"
                : "Only screens mapped to this Con. Area"
            }}</span>
          </span>
        </button>
      </div>

      <div class="ct-loading" v-else>Loading counter types…</div>

      <div class="ct-empty" v-if="!isLoading && !counterTypeOptions.length">
        No counter types found.
      </div>

      <div class="ct-actions">
        <button
          class="btn-blue ct-btn-primary"
          :disabled="isSaving || isLoading"
          @click="saveCounterType"
        >
          {{ isSaving ? "Saving…" : "Apply" }}
        </button>
        <button
          type="button"
          class="ct-btn-secondary"
          :disabled="isSaving"
          @click.prevent.stop="$bvModal.hide(modalId)"
        >
          Cancel
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import PosService from "@/services/PosService";
import { EventBus } from "@/main.js";
import {
  COUNTER_TYPE_SCOPE,
  getCounterTypeFromCookie,
  setCounterTypeCookie,
  clearCounterTypeCookie,
  clearCounterTypeRelatedCache,
} from "@/helper/posSevenStarConfig";

export default {
  props: {
    // "schedule" | "fnb" — independent selections
    scope: {
      type: String,
      default: COUNTER_TYPE_SCOPE.SCHEDULE,
      validator: (value) =>
        [COUNTER_TYPE_SCOPE.SCHEDULE, COUNTER_TYPE_SCOPE.FNB, "schedule", "fnb"].includes(
          value
        ),
    },
  },
  data() {
    return {
      isSaving: false,
      isLoading: false,
      selectedCounterType: null,
      counterTypeOptions: [],
    };
  },
  computed: {
    resolvedScope() {
      return this.scope === COUNTER_TYPE_SCOPE.FNB || this.scope === "fnb"
        ? COUNTER_TYPE_SCOPE.FNB
        : COUNTER_TYPE_SCOPE.SCHEDULE;
    },
    isFnbScope() {
      return this.resolvedScope === COUNTER_TYPE_SCOPE.FNB;
    },
    modalId() {
      return this.isFnbScope
        ? "fnbCounterTypeSettingModal"
        : "scheduleCounterTypeSettingModal";
    },
    modalTitle() {
      return this.isFnbScope ? "FNB Counter Type" : "Schedule Counter Type";
    },
  },
  mounted() {
    this.loadCounterTypes();
  },
  methods: {
    selectAll() {
      this.selectedCounterType = null;
    },
    selectOption(option) {
      this.selectedCounterType = option;
    },
    syncSelectionFromCookie() {
      const { counter_type_id } = getCounterTypeFromCookie(this.resolvedScope);
      if (!counter_type_id) {
        this.selectedCounterType = null;
        return;
      }
      const matched = this.counterTypeOptions.find(
        (opt) => String(opt.fct_id) === String(counter_type_id)
      );
      this.selectedCounterType = matched || null;
    },
    async onModalShown() {
      try {
        await this.loadCounterTypes();
        this.syncSelectionFromCookie();
      } catch (error) {
        this.$toasted.show("Unable to load counter types.");
      }
    },
    async loadCounterTypes() {
      this.isLoading = true;
      try {
        const response = await PosService.getAllCounterTypes();
        const counterTypes =
          (response && response.data && response.data.Records) || [];

        this.counterTypeOptions = counterTypes
          .map((item) => {
            if (item && typeof item === "object") {
              return {
                fct_id: item.fct_id || item.counter_type_id || item.id,
                counter_type:
                  item.counter_name ||
                  item.counter_type_name ||
                  item.counter_type_code ||
                  item.counter_type ||
                  item.name ||
                  item.value ||
                  item.label ||
                  item.counterType ||
                  item.counterName ||
                  "",
              };
            }
            return { fct_id: null, counter_type: item };
          })
          .filter((opt) => opt.counter_type && opt.fct_id != null);

        this.syncSelectionFromCookie();
      } catch (error) {
        this.$toasted.show("Unable to load counter types.");
      } finally {
        this.isLoading = false;
      }
    },
    async saveCounterType() {
      try {
        this.isSaving = true;
        const scope = this.resolvedScope;

        if (this.selectedCounterType && this.selectedCounterType.fct_id != null) {
          setCounterTypeCookie(this.selectedCounterType, scope);
          this.$toasted.show(
            this.isFnbScope
              ? `FNB: showing ${this.selectedCounterType.counter_type} items.`
              : `Schedule: showing ${this.selectedCounterType.counter_type} showtimes.`
          );
        } else {
          clearCounterTypeCookie(scope);
          this.$toasted.show(
            this.isFnbScope
              ? "FNB: showing all items."
              : "Schedule: showing all showtimes."
          );
        }

        clearCounterTypeRelatedCache(scope);

        EventBus.$emit("COUNTER_TYPE_CHANGED", {
          scope,
          counterType: this.selectedCounterType,
        });
        this.$bvModal.hide(this.modalId);
      } catch (error) {
        this.$toasted.show("Unable to save counter type.");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style>
#scheduleCounterTypeSettingModal___BV_modal_content_,
#fnbCounterTypeSettingModal___BV_modal_content_ {
  width: 560px !important;
  max-width: 94vw;
  margin: auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

#scheduleCounterTypeSettingModal___BV_modal_header_,
#fnbCounterTypeSettingModal___BV_modal_header_ {
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px 12px;
}

#scheduleCounterTypeSettingModal___BV_modal_title_,
#fnbCounterTypeSettingModal___BV_modal_title_ {
  width: 100%;
  text-align: left;
  color: var(--blue);
  font-weight: 700;
  font-size: 18px;
  margin: 0;
}

.counter-type-modal-body {
  padding: 12px 20px 20px !important;
}

.ct-help {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #666;
  font-weight: 400;
}

.ct-help strong {
  color: #333;
  font-weight: 600;
}

.ct-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-height: none;
  overflow: visible;
  padding-right: 0;
  margin-bottom: 16px;
}

.ct-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1.5px solid #e2e2e2;
  background: #fff;
  border-radius: 10px;
  padding: 12px 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
  min-height: 72px;
}

.ct-option:hover {
  border-color: var(--blue);
  background: #f7faff;
}

.ct-option.active {
  border-color: var(--blue);
  background: #eef4ff;
  box-shadow: 0 0 0 1px var(--blue);
}

.ct-option-radio {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #b0b0b0;
  position: relative;
  background: #fff;
}

.ct-option.active .ct-option-radio {
  border-color: var(--blue);
}

.ct-option.active .ct-option-radio::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--blue);
  transform: translate(-50%, -50%);
}

.ct-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ct-option-title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  line-height: 1.3;
}

.ct-option.active .ct-option-title {
  color: var(--blue);
}

.ct-option-sub {
  font-size: 11px;
  font-weight: 400;
  color: #777;
  line-height: 1.35;
}

.ct-loading,
.ct-empty {
  padding: 24px 8px;
  text-align: center;
  color: #777;
  font-size: 13px;
}

.ct-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  padding-top: 4px;
}

.ct-btn-primary {
  min-width: 100px;
}

.ct-btn-secondary {
  min-width: 90px;
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1.5px solid #c8c8c8;
  background: #fff;
  color: #444;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.ct-btn-secondary:hover:not(:disabled) {
  border-color: #999;
  background: #f5f5f5;
}

.ct-btn-secondary:disabled,
.ct-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/Partials/CounterTypePopup.vue